import { Icons } from '@/assets/icons/icons';
import { Command } from '@/components/ui/cmdk/cmdk';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { highlightMatches, type AgentPickerRow } from './agent-picker-blocks';
import { ModelLogo } from './model-logo';

const Highlighted = ({
  text,
  tokens,
  matchClassName,
}: {
  text: string;
  tokens: string[];
  matchClassName: string;
}) =>
  highlightMatches(text, tokens).map((part, index) =>
    part.match ? (
      <span key={index} className={matchClassName}>
        {part.text}
      </span>
    ) : (
      part.text
    )
  );

/** One agent: logo, then its name and version over its role and agent key */
export const AgentPickerRowItem = ({
  row,
  tokens,
  selected,
  onSelect,
}: {
  row: AgentPickerRow;
  tokens: string[];
  selected: boolean;
  onSelect: () => void;
}) => {
  // An agent without an alias, or a mode like `default`, is named by its key already
  const showKey = row.agent !== row.name;

  return (
    <Command.Item
      value={row.mode}
      onSelect={onSelect}
      data-current={selected}
      className="items-start gap-2.5 py-1.5 text-sm"
    >
      <ModelLogo
        company={row.company}
        className="mt-0.5 size-4 shrink-0 text-foreground-subtle"
      />
      <YStack className="min-w-0 flex-1 gap-0.5">
        <XStack className="min-w-0 items-baseline gap-2">
          <span className="truncate">
            <Highlighted
              text={row.name}
              tokens={tokens}
              matchClassName="font-semibold text-foreground"
            />
          </span>
          {row.version && (
            <span className="shrink-0 text-xs text-foreground-muted">
              V{row.version}
            </span>
          )}
        </XStack>
        {(row.role || showKey) && (
          <XStack className="min-w-0 gap-1.5 text-xs text-foreground-muted">
            {row.role && (
              <span className="shrink-0 text-foreground-subtle">
                {row.role}
              </span>
            )}
            {row.role && showKey && <span aria-hidden>·</span>}
            {showKey && (
              // The colour tokens are plain CSS variables, so Tailwind's `/50` modifier emits nothing for them
              <span className="truncate opacity-50">
                <Highlighted
                  text={row.agent}
                  tokens={tokens}
                  matchClassName="text-foreground"
                />
              </span>
            )}
          </XStack>
        )}
      </YStack>
      <span
        className={cn('mt-0.5 flex size-4 shrink-0', !selected && 'invisible')}
      >
        <Icons.check className="size-4" />
      </span>
    </Command.Item>
  );
};
